"use client";

import React, { useEffect, useRef, useState } from 'react';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
interface StatItemProps {
  end: number;
  suffix: string;
  label: string;
  caption: string;
  index: number;
}
function StatItem({
  end,
  suffix,
  label,
  caption,
  index
}: StatItemProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.5
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    const duration = 2000;
    let animationFrameId: number;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeProgress * end));
      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };
    animationFrameId = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [isVisible, end]);
  return <div ref={ref} data-index={index} className="flex flex-col items-center text-center px-4 py-6 sm:py-2">
            <div className="text-5xl md:text-6xl font-bold text-foreground mb-3 tracking-tighter drop-shadow-sm">
                {count}{suffix}
            </div>
            <div className="text-lg md:text-xl font-semibold text-primary mb-2 uppercase tracking-wide">
                {label}
            </div>
            <div className="text-sm text-muted-foreground max-w-[200px] leading-relaxed">
                {caption}
            </div>
        </div>;
}
export default function LocalExpertiseGlobalStandards() {
  const stats = [{
    end: 20,
    suffix: '+',
    label: 'Properties Managed',
    caption: 'Across the Kansas City Metro area'
  }, {
    end: 98,
    suffix: '%',
    label: 'Occupancy Rate',
    caption: 'Consistently high tenant retention'
  }, {
    end: 15,
    suffix: '+',
    label: 'Years Experience',
    caption: 'Deep local market knowledge'
  }, {
    end: 100,
    suffix: '%',
    label: 'Transparent',
    caption: 'Real-time financial reporting access'
  }];
  return <section id="local-expertise-global-standards" className="dark bg-background text-foreground py-16 md:py-24 border-y border-border/40 relative overflow-hidden">
            {}
            <div className="absolute inset-0 bg-muted/5 pointer-events-none" />
            
            <div className="container mx-auto px-4 relative z-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 divide-y sm:divide-y-0 sm:divide-x divide-border/30">
                    {stats.map((stat, i) => <StatItem key={i} index={i} {...stat} />)}
                </div>
            </div>
        </section>;
}